import { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useLocation } from 'react-router-dom';
import Hamburger from 'hamburger-react';
import { theme } from '../config/theme';
import { Button } from '../junto-frontend/components/ui';
import { getComponent } from '../config/imageRegistry';
import { purchaseOptions } from '../junto-frontend/components/PurchaseSystem';
import useScrollToTop from '../hooks/useScrollToTop';
import { useBackButton } from '../contexts/BackButtonContext';
import { useAuth } from '../junto-frontend/contexts/ClerkAuthContext';
import { usePopup } from '../junto-frontend/contexts/PopupContext';
import { useClerk } from '@clerk/clerk-react';

const Nav = styled.nav`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  background-color: ${props => (props.$scrolled || props.$forceBackground) ? theme.shadows.navBackground : 'transparent'};
  backdrop-filter: ${props => (props.$scrolled || props.$forceBackground) ? 'blur(10px)' : 'none'};
  z-index: 1000;
  transition: background-color 0.3s ease, backdrop-filter 0.3s ease;
  padding: ${theme.spacing.lg} ${theme.spacing.layoutGutter};
  will-change: background-color, backdrop-filter;

  @media (max-width: ${theme.breakpoints.tablet}) {
    background-color: ${theme.shadows.navBackground};
    backdrop-filter: blur(10px);
    padding: ${theme.spacing.md} ${theme.spacing.layoutGutter};
  }

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 1px;
    background-color: ${theme.colors.accent.bright};
  }
`;

const NavContainer = styled.div`
  max-width: 1440px;
  margin: 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    justify-content: center;
    position: relative;
  }
`;

const Logo = styled(Link)`
  display: flex;
  align-items: center;
  justify-content: center;
  text-decoration: none;
  transition: ${theme.transitions.default};
  height: 100%;
  
  &:hover {
    svg {
      *, g, path {
        fill: ${props => props.$isTransparentPage && !props.$scrolled ? theme.colors.accent.bright : theme.colors.accent.main} !important;
      }
    }
  }
`;

const LogoSVG = getComponent('site_logo');

const StyledLogoSVG = styled(LogoSVG)`
  height: 64px;
  width: auto;
  
  *, g, path {
    fill: ${props => props.$isTransparentPage && !props.$scrolled ? theme.colors.neutral.lightest : theme.colors.primary.ui_dark} !important;
    transition: ${theme.transitions.default};
  }

  @media (max-width: ${theme.breakpoints.tablet}) {
    *, g, path {
      fill: ${theme.colors.primary.ui_dark} !important;
    }
  }
`;

const NavLinks = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.xl};

  @media (max-width: ${theme.breakpoints.tablet}) {
    display: none;
  }
`;

const NavItem = styled.div`
  position: relative;
`;

const NavLink = styled(Link)`
  font-family: ${theme.typography.navigation.fontFamily};
  font-size: calc(${theme.typography.navigation.fontSize} * 1.2);
  font-weight: ${theme.typography.navigation.fontWeight};
  letter-spacing: ${theme.typography.navigation.letterSpacing};
  text-transform: ${theme.typography.navigation.textTransform};
  color: ${props => props.$isTransparentPage && !props.$scrolled ? theme.colors.neutral.lightest : theme.colors.primary.text};
  position: relative;
  padding: ${theme.spacing.sm} 0;
  text-decoration: none;
  transition: ${theme.transitions.default};
  display: flex;
  align-items: center;
  gap: 4px;
  
  &::after {
    content: '';
    position: absolute;
    bottom: 4px;
    left: 50%;
    transform: translateX(-50%);
    width: 0;
    height: 2px;
    background-color: ${props => props.$isTransparentPage && !props.$scrolled ? theme.colors.neutral.lightest : theme.colors.primary.text};
    transition: width 0.3s ease;
  }

  &:hover::after {
    width: 100%;
  }

  @media (max-width: ${theme.breakpoints.tablet}) {
    color: ${theme.colors.primary.text};
    
    &:after {
      background-color: ${theme.colors.primary.text};
    }
    
    &:hover:after {
      width: 30%;
    }
  }
`;

const ExternalNavLink = styled.a`
  font-family: ${theme.typography.navigation.fontFamily};
  font-size: ${theme.typography.navigation.fontSize};
  font-weight: ${theme.typography.navigation.fontWeight};
  letter-spacing: ${theme.typography.navigation.letterSpacing};
  text-transform: ${theme.typography.navigation.textTransform};
  color: ${props => props.$isTransparentPage && !props.$scrolled ? theme.colors.neutral.lightest : theme.colors.primary.text};
  position: relative;
  padding: ${theme.spacing.sm} 0;
  text-decoration: none;
  transition: ${theme.transitions.default};
  display: flex;
  align-items: center;
  gap: 4px;

  &:hover {
    font-weight: bold;
    color: ${props => props.$isTransparentPage && !props.$scrolled ? theme.colors.accent.bright : theme.colors.accent.main};
  }

  @media (max-width: ${theme.breakpoints.tablet}) {
    color: ${theme.colors.primary.text};
  }
`;

const CaretIcon = styled.span`
  font-size: 0.7rem;
  transition: transform 0.3s ease;
  display: inline-block;
  transform: ${props => {
    if (props.$isMobile) {
      return props.$isOpen ? 'rotate(0deg)' : 'rotate(-90deg)';
    }
    return props.$isOpen ? 'rotate(180deg)' : 'rotate(0deg)';
  }};
`;

const Dropdown = styled(motion.div)`
  position: absolute;
  top: calc(100% + 12px);
  left: 50%;
  margin-left: -90px;
  background-color: ${props => (props.$scrolled || props.$forceBackground) ? theme.shadows.navBackground : 'rgba(246, 244, 243, 0.95)'};
  backdrop-filter: blur(10px);
  border-radius: 4px;
  box-shadow: 0 10px 40px ${theme.shadows.strong};
  padding: ${theme.spacing.sm} 0;
  width: 180px;
  border: 1px solid ${props => (props.$scrolled || props.$forceBackground) ? theme.shadows.light : 'rgba(246, 244, 243, 0.2)'};
  overflow: hidden;
  z-index: 1001;
  white-space: nowrap;
`;

const MobileDropdownContainer = styled.div`
  margin-top: ${theme.spacing.xs};
`;

const DropdownItem = styled.a`
  display: block;
  padding: ${theme.spacing.md};
  color: ${theme.colors.primary.text};
  font-family: ${theme.typography.navigation.fontFamily};
  font-size: calc(${theme.typography.navigation.fontSize} * 1.0);
  font-weight: ${theme.typography.navigation.fontWeight};
  letter-spacing: ${theme.typography.navigation.letterSpacing};
  text-transform: ${theme.typography.navigation.textTransform};
  transition: ${theme.transitions.default};
  text-decoration: none;
  text-align: center;
  position: relative;

  &:after {
    content: '';
    position: absolute;
    bottom: calc(${theme.spacing.md} - 4px);
    left: 50%;
    transform: translateX(-50%);
    width: 0;
    height: 2px;
    background-color: ${theme.colors.primary.text};
    transition: width 0.3s ease;
  }

  &:hover:after {
    width: 80%;
  }
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    padding: calc(${theme.spacing.md} * 0.53);
    
    &:after {
      bottom: calc(calc(${theme.spacing.md} * 0.67) - 4px);
    }
    
    &:hover:after {
      width: 30%;
    }
  }
`;

const NavActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.md};
  height: 100%;
`;

const NavigationButton = styled(Button)`
  padding: 18px 36px;
  font-family: ${theme.typography.button.fontFamily};
  font-size: ${theme.typography.button.fontSize};
  font-weight: ${theme.typography.button.fontWeight};
  letter-spacing: ${theme.typography.button.letterSpacing};
  text-transform: ${theme.typography.button.textTransform};
  border-color: ${props => props.$isTransparentPage && !props.$scrolled ? theme.colors.neutral.lightest : theme.colors.primary.buttons};
  color: ${props => props.$isTransparentPage && !props.$scrolled ? theme.colors.neutral.lightest : theme.colors.primary.buttons};
  
  &:hover:not(:disabled) {
    background: ${props => props.$isTransparentPage && !props.$scrolled ? theme.colors.neutral.lightest : theme.colors.primary.buttons} !important;
    color: ${props => props.$isTransparentPage && !props.$scrolled ? theme.colors.primary.buttons : theme.colors.primary.background} !important;
    transform: translateY(-2px);
  }
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    display: none;
  }
`;

const MobileNavigationButton = styled(Button)`
  padding: 14px 28px;
  font-family: ${theme.typography.button.fontFamily};
  font-size: ${theme.typography.button.fontSize};
  font-weight: ${theme.typography.button.fontWeight};
  letter-spacing: ${theme.typography.button.letterSpacing};
  text-transform: ${theme.typography.button.textTransform};
  border-color: ${theme.colors.primary.buttons};
  color: ${theme.colors.primary.buttons};
  margin-top: ${props => props.$marginTop || '0'};
  
  &:hover {
    background: ${theme.colors.primary.buttons};
    color: ${theme.colors.primary.background};
  }
`;

const MobileBackButton = styled(Link)`
  display: none;

  @media (max-width: ${theme.breakpoints.tablet}) {
    display: ${props => props.$show ? 'block' : 'none'};
    position: absolute;
    left: ${theme.spacing.layoutGutter};
    top: 50%;
    transform: translateY(-50%);
    color: ${theme.colors.primary.text};
    text-decoration: none;
    font-family: ${theme.typography.navigation.fontFamily};
    font-size: ${theme.typography.navigation.fontSize};
    font-weight: ${theme.typography.navigation.fontWeight};
    letter-spacing: ${theme.typography.navigation.letterSpacing};
    transition: ${theme.transitions.default};

    &:hover {
      color: ${theme.colors.accent.main};
    }
  }
`;

const MobileMenuButton = styled.div`
  display: none;

  @media (max-width: ${theme.breakpoints.tablet}) {
    display: block;
    position: fixed;
    right: ${theme.spacing.layoutGutter};
    top: ${theme.spacing.md};
    z-index: 1002;
  }
`;

const MobileMenu = styled(motion.div)`
  position: fixed;
  top: var(--nav-height, 80px);
  left: 0;
  width: 100%;
  height: calc(100vh - var(--nav-height, 80px));
  background-color: ${theme.colors.primary.background};
  padding: ${theme.spacing.lg} ${theme.spacing.layoutGutter} ${theme.spacing.md};
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 0;
  overflow-y: auto;
  z-index: 999;
`;

const MobileMenuItem = styled.div`
  width: 100%;
  padding: calc(${theme.spacing.md} * 0.67) 0;
  border-bottom: 1px solid ${theme.colors.accent.bright};
  
  &:last-of-type {
    border-bottom: none;
  }
`;

const MobileSectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
`;

const MobileNavLink = styled(NavLink)`
  @media (max-width: ${theme.breakpoints.tablet}) {
    &:hover:after {
      width: 100%;
    }
  }
`;

const MobileCaretButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: inherit;
  transition: ${theme.transitions.default};
  
  &:hover {
    color: ${theme.colors.accent.main};
  }
`;

const Navigation = () => {
  const [scrolled, setScrolled] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileExpandedSections, setMobileExpandedSections] = useState({
    'About': false,      // Collapsed by default
    'Bookings': true,    // Expanded by default
    'Purchase': true     // Expanded by default
  });
  const navRef = useRef(null);
  const location = useLocation();
  const scrollToTop = useScrollToTop(false); // Get manual scroll function
  const { backButtonInfo } = useBackButton();
  const { user, isAuthenticated } = useAuth();
  const { openAccountPopup } = usePopup();
  const { openSignIn } = useClerk();
  const isHomePage = location.pathname === '/';
  const isBookPage = location.pathname === '/book';
  const isTransparentPage = isHomePage && !isBookPage;

  useEffect(() => {
    // Set initial scroll state
    setScrolled(window.scrollY > 50);
    
    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          setScrolled(window.scrollY > 50);
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [mobileMenuOpen]);

  // Dynamically set navigation height CSS custom properties
  useEffect(() => {
    const updateNavHeight = () => {
      if (navRef.current) {
        const height = navRef.current.offsetHeight;
        document.documentElement.style.setProperty('--nav-height', `${height}px`);
      }
    };

    // Update immediately
    updateNavHeight();
    
    // Update on window resize
    window.addEventListener('resize', updateNavHeight);
    
    return () => {
      window.removeEventListener('resize', updateNavHeight);
    };
  }, [mobileMenuOpen, scrolled]); // Re-measure when nav state changes

  const handleAuthButtonClick = () => {
    if (isAuthenticated) {
      openAccountPopup();
    } else {
      // Use Clerk's hosted sign-in page
      openSignIn();
    }
  };

  const cropEmail = (email, maxLength = 20) => {
    if (!email) return '';
    if (email.length <= maxLength) return email;
    return email.substring(0, maxLength - 3) + '...';
  };

  const getButtonText = () => {
    if (isAuthenticated && user?.email) {
      return cropEmail(user.email);
    }
    return 'Login / Signup';
  };

  const toggleMobileSection = (sectionName) => {
    setMobileExpandedSections(prev => ({
      ...prev,
      [sectionName]: !prev[sectionName]
    }));
  };

  const navItems = [
    {
      name: 'About',
      href: '/',
      dropdown: [
        { name: 'Our Studio', href: '/#studio' },
        { name: 'Our Team', href: '/#team' },
        { name: 'Our Movements', href: '/#movements' },
        { name: 'Location / Hours', href: '/location' },
        { name: 'Join Our Team', href: '/join-our-team' },
      ],
    },
    {
      name: 'Bookings',
      href: '/book',
      dropdown: [
        { name: 'Group Classes', href: '/book/group' },
        { name: 'Private Classes', href: '/book/private' },
        { name: 'Events', href: '/book/events' },
      ],
    },
    {
      name: 'Purchase',
      href: '/purchase',
      dropdown: purchaseOptions.map(option => ({
        name: option.name,
        href: `/purchase#${option.id}`
      })),
    },
    { name: <i className="fa fa-instagram"></i>, href: 'https://www.instagram.com/the.sol.room/', external: true },
  ];

  return (
    <Nav ref={navRef} $scrolled={scrolled} $forceBackground={isBookPage}>
      <NavContainer>
        <MobileBackButton 
          to={backButtonInfo.link || "/"}
          $show={!!backButtonInfo.text}
          onClick={(e) => {
            if (mobileMenuOpen) {
              e.preventDefault();
              setMobileMenuOpen(false);
            }
          }}
          style={{ 
            opacity: mobileMenuOpen ? 0 : 1,
            pointerEvents: mobileMenuOpen ? 'none' : 'auto',
            transition: 'opacity 0.3s ease'
          }}
        >
          ← Back
        </MobileBackButton>

        <Logo 
          to="/" 
          $isTransparentPage={isTransparentPage} 
          $scrolled={scrolled}
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        >
          <StyledLogoSVG $isTransparentPage={isTransparentPage} $scrolled={scrolled} />
        </Logo>
        
        <NavLinks>
          {navItems.map((item) => (
            <NavItem
              key={item.name}
              onMouseEnter={() => setActiveDropdown(item.name)}
              onMouseLeave={() => setActiveDropdown(null)}
            >
              {item.external ? (
                <ExternalNavLink 
                  href={item.href} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  $isTransparentPage={isTransparentPage} 
                  $scrolled={scrolled}
                >
                  {item.name}
                </ExternalNavLink>
              ) : (
                <NavLink to={item.href} $isTransparentPage={isTransparentPage} $scrolled={scrolled}>
                  {item.name}
                  {item.dropdown && (
                    <CaretIcon $isOpen={activeDropdown === item.name}>▼</CaretIcon>
                  )}
                </NavLink>
              )}
              
              <AnimatePresence>
                {activeDropdown === item.name && item.dropdown && (
                  <Dropdown
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.2 }}
                    $scrolled={scrolled}
                    $forceBackground={isBookPage}
                  >
                    {item.dropdown.map((dropdownItem) => (
                      <DropdownItem key={dropdownItem.name} href={dropdownItem.href}>
                        {dropdownItem.name}
                      </DropdownItem>
                    ))}
                  </Dropdown>
                )}
              </AnimatePresence>
            </NavItem>
          ))}
        </NavLinks>

        <NavActions>
          <NavigationButton 
            onClick={handleAuthButtonClick}
            $isTransparentPage={isTransparentPage}
            $scrolled={scrolled}
          >
            {getButtonText()}
          </NavigationButton>
          
          <MobileMenuButton>
            <Hamburger 
              toggled={mobileMenuOpen} 
              toggle={setMobileMenuOpen}
              size={24}
              color={theme.colors.primary.ui_dark}
              duration={0.3}
            />
          </MobileMenuButton>
        </NavActions>
      </NavContainer>

      <AnimatePresence>
        {mobileMenuOpen && (
          <MobileMenu
            initial={{ opacity: 0, x: '100%' }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: '100%' }}
            transition={{ type: 'tween', duration: 0.3 }}
          >
            {navItems.filter(item => !item.external).map((item) => {
              const isExpanded = mobileExpandedSections[item.name] !== undefined 
                ? mobileExpandedSections[item.name] 
                : true;
              
              return (
                <MobileMenuItem key={item.name}>
                  {item.dropdown ? (
                    <>
                      <MobileSectionHeader>
                        <MobileNavLink 
                          to={item.href}
                          $isTransparentPage={isTransparentPage} 
                          $scrolled={scrolled}
                          onClick={() => {
                            setMobileMenuOpen(false);
                            if (!item.href.includes('#')) {
                              setTimeout(() => {
                                window.scrollTo({ top: 0, behavior: 'smooth' });
                              }, 100);
                            }
                          }}
                        >
                          {item.name}
                        </MobileNavLink>
                        <MobileCaretButton
                          onClick={() => toggleMobileSection(item.name)}
                        >
                          <CaretIcon 
                            className="caret-icon"
                            $isOpen={isExpanded}
                            $isMobile={true}
                          >
                            ▼
                          </CaretIcon>
                        </MobileCaretButton>
                      </MobileSectionHeader>
                      <AnimatePresence>
                        {isExpanded && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.3, ease: 'easeInOut' }}
                            style={{ overflow: 'hidden' }}
                          >
                            <MobileDropdownContainer>
                              {item.dropdown.map((dropdownItem) => (
                                <DropdownItem 
                                  key={dropdownItem.name} 
                                  href={dropdownItem.href} 
                                  onClick={() => {
                                    setMobileMenuOpen(false);
                                    // Scroll to top unless it's an anchor link
                                    if (!dropdownItem.href.includes('#')) {
                                      setTimeout(scrollToTop, 100);
                                    }
                                  }}
                                >
                                  {dropdownItem.name}
                                </DropdownItem>
                              ))}
                            </MobileDropdownContainer>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </>
                  ) : (
                    <MobileNavLink 
                      to={item.href} 
                      $isTransparentPage={isTransparentPage} 
                      $scrolled={scrolled} 
                      onClick={() => {
                        setMobileMenuOpen(false);
                        // Scroll to top unless it's an anchor link
                        if (!item.href.includes('#')) {
                          setTimeout(() => {
                            window.scrollTo({ top: 0, behavior: 'smooth' });
                          }, 100);
                        }
                      }} 
                      style={{ justifyContent: 'center', gap: '4px' }}
                    >
                      {item.name}
                    </MobileNavLink>
                  )}
                </MobileMenuItem>
              );
            })}
            
            <MobileNavigationButton 
              onClick={() => {
                handleAuthButtonClick();
                setMobileMenuOpen(false);
                // Don't scroll to top for modal opening
              }}
              $marginTop={theme.spacing.sm}
            >
              {getButtonText()}
            </MobileNavigationButton>
          </MobileMenu>
        )}
      </AnimatePresence>
    </Nav>
  );
};

export default Navigation;
export { NavigationButton };